import { useMemo } from 'react';
import constants from '../../../../config/constants';

const { PAGES } = constants;

/**
 * Custom hook that encapsulates all business logic for ImageGalleryAdditionalButtons
 * Handles visibility logic for MyLists and Share buttons based on page context and product state
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.appCtx - Application context with site configuration
 * @param {string} params.productId - Product identifier
 * @param {string} params.variantId - Product variant identifier
 * @param {string} params.offeringId - Product offering identifier
 * @param {boolean} params.showMyList - Flag to show MyLists functionality
 * @param {string} params.page - Current page context (PDP, etc.)
 * @param {boolean} params.isOutOfStock - Product out of stock status
 * @returns {Object} - Processed data and computed values for component rendering
 */
export const useImageGalleryAdditionalButtons = ({
  appCtx,
  productId,
  variantId,
  offeringId,
  showMyList,
  page,
  isOutOfStock,
}) => {
  const { siteConfig } = appCtx || {};
  const { toggles } = siteConfig || {};
  const { isPDPMyListsEnabled = false } = toggles || {};

  // Page context validation
  const isPDP = useMemo(() => page === PAGES.PDP, [page]);

  // MyLists feature configuration
  const isMyListsEnabled = useMemo(() => isPDPMyListsEnabled && showMyList, [isPDPMyListsEnabled, showMyList]);

  // Visibility logic for MyLists button
  const shouldShowMyLists = useMemo(
    () => isPDP && isMyListsEnabled && !isOutOfStock,
    [isPDP, isMyListsEnabled, isOutOfStock]
  );

  // Visibility logic for Share button
  const shouldShowShareIcon = useMemo(() => isPDP && !isOutOfStock, [isPDP, isOutOfStock]);

  // Component should render if any button is visible
  const shouldRender = useMemo(
    () => !isOutOfStock && isPDP && (shouldShowMyLists || shouldShowShareIcon),
    [isOutOfStock, isPDP, shouldShowMyLists, shouldShowShareIcon]
  );

  // Product data for MyLists component
  const productData = useMemo(() => ({ productId, variantId, offeringId }), [productId, variantId, offeringId]);

  // Event handlers for button interactions
  const handleButtonClick = useMemo(
    () => (e) => {
      e.stopPropagation();
    },
    []
  );

  const handleButtonKeyDown = useMemo(
    () => (e) => {
      e.stopPropagation();
    },
    []
  );

  return {
    // Computed visibility flags
    shouldRender,
    shouldShowMyLists,
    shouldShowShareIcon,
    isPDP,

    // Data objects
    productData,
    appCtx,

    // Event handlers
    handleButtonClick,
    handleButtonKeyDown,

    // Configuration flags
    isMyListsEnabled,
    isOutOfStock,
  };
};
